import { ArrowDownwardRounded, ArrowUpwardRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Card,
  Chip,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import { Resources } from '../resource';
import { displayLabel } from '../perps-v1-shared/api';
import { PERPS_V1_VIEWS_PERMISSION } from '../perps-v1-shared/constants';
import { errorMessage } from '../perps-v1-shared/errorMessage';
import { MarketView } from '../perps-v1-shared/types';
import { buildChildrenByParent } from '../perps-v1-shared/viewTree';
import ViewEditorDialog from './components/ViewEditorDialog';
import ViewSymbolsDialog from './components/ViewSymbolsDialog';
import { viewsApi } from './dataProvider';
import './init';
import { SORT_DIRECTION_LABEL_KEYS, SORT_FIELD_LABEL_KEYS } from './viewForm';

interface ParentOption {
  code: string;
  label: string;
  depth: number;
}

// dexes/sectorCodes 规则摘要，空字符串 dex 元素代表 native
function ruleSummary(view: MarketView, translate: (key: string) => string): string {
  const parts: string[] = [];
  if (view.dexes && view.dexes.length > 0) {
    parts.push(
      view.dexes.map((d) => d || translate('module.perpsV1Views.fields.native')).join(', ')
    );
  }
  if (view.sectorCodes && view.sectorCodes.length > 0) {
    parts.push(view.sectorCodes.join(', '));
  }
  return parts.length > 0 ? parts.join(' · ') : '-';
}

function sortSummary(view: MarketView, translate: (key: string) => string): string {
  const field = translate(SORT_FIELD_LABEL_KEYS[view.sortField] ?? view.sortField);
  const direction = translate(SORT_DIRECTION_LABEL_KEYS[view.sortDirection] ?? view.sortDirection);
  return `${field} · ${direction}`;
}

// Next slot at the END of a sibling group: max(existing displayOrder) + 1, or 1 for an empty
// group. Used to seed a new view's displayOrder now that the editor dialog no longer has a
// Display order input — matches the ↑/↓ arrows' own sibling grouping (childrenByParent), so a
// new view never collides with (or leaves a gap before) an existing sibling.
function nextDisplayOrder(siblings: MarketView[] | undefined): number {
  if (!siblings || siblings.length === 0) return 1;
  return Math.max(...siblings.map((v) => v.displayOrder)) + 1;
}

const View = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [views, setViews] = useState<MarketView[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState<MarketView | null>(null);
  // Implied parent when creating via the row-level "Add child view" action; null means the
  // dialog is either editing (see editingView) or creating a top-level view. Reset on every
  // open path below so a previous "Add child" click can never leak into a later top-level create.
  const [createParentView, setCreateParentView] = useState<MarketView | null>(null);
  // displayOrder to seed the create form with — see nextDisplayOrder. Computed at the moment
  // "New view" / "Add child view" is clicked (openCreate/openCreateChild), from whichever
  // sibling group the new view will join.
  const [createDisplayOrder, setCreateDisplayOrder] = useState(1);
  const [symbolsDialogOpen, setSymbolsDialogOpen] = useState(false);
  const [managingView, setManagingView] = useState<MarketView | null>(null);

  const loadViews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dataProvider.getList(Resources.perpsV1Views, {
        filter: {},
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'displayOrder', order: 'ASC' },
      });
      const rows: MarketView[] = response?.data ?? [];
      setViews(rows);
      return rows;
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Views.loadError')),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify, translate]);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  const childrenByParent = useMemo(() => buildChildrenByParent(views), [views]);
  const topLevel = childrenByParent.get(null) ?? [];

  // 父级候选：仅顶层与二级视图可作父级（保证树最多三层），按树形顺序展开
  const parentOptions = useMemo<ParentOption[]>(() => {
    const result: ParentOption[] = [];
    const walk = (list: MarketView[] | undefined, depth: number) => {
      if (depth > 1) return;
      (list ?? []).forEach((v) => {
        result.push({ code: v.code, label: displayLabel(v.label, v.code), depth });
        walk(childrenByParent.get(v.code), depth + 1);
      });
    };
    walk(childrenByParent.get(null), 0);
    return result;
  }, [childrenByParent]);

  // 子代 code 映射（parentCode -> childCodes），供编辑弹窗排除自身及子树
  const childrenCodes = useMemo(() => {
    const record: Record<string, string[]> = {};
    childrenByParent.forEach((list, key) => {
      if (key) record[key] = list.map((v) => v.code);
    });
    return record;
  }, [childrenByParent]);

  const handleMove = async (view: MarketView, direction: -1 | 1) => {
    const siblings = childrenByParent.get(view.parentCode ?? null) ?? [];
    const index = siblings.findIndex((s) => s.code === view.code);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= siblings.length) return;
    const reordered = [...siblings];
    [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
    try {
      await viewsApi.reorder(view.parentCode ?? null, reordered.map((s) => s.code));
      await loadViews();
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Views.reorderError')),
        { type: 'error' }
      );
    }
  };

  const handleToggleEnabled = async (view: MarketView, enabled: boolean) => {
    try {
      await viewsApi.setEnabled(view.code, enabled);
      await loadViews();
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Shared.saveError')),
        { type: 'error' }
      );
    }
  };

  const handleDelete = async (view: MarketView) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(translate('module.perpsV1Shared.confirmDelete'))) return;
    try {
      await viewsApi.remove(view.code);
      notify(translate('module.perpsV1Shared.saveSuccess'), { type: 'success' });
      await loadViews();
    } catch (error: any) {
      notify(
        errorMessage(error, translate('module.perpsV1Shared.saveError')),
        { type: 'error' }
      );
    }
  };

  const openCreate = () => {
    setEditingView(null);
    setCreateParentView(null);
    // Top-level siblings: end of the null-parentCode group.
    setCreateDisplayOrder(nextDisplayOrder(childrenByParent.get(null)));
    setDialogOpen(true);
  };

  const openCreateChild = (parent: MarketView) => {
    setEditingView(null);
    setCreateParentView(parent);
    // End of this row's own children group.
    setCreateDisplayOrder(nextDisplayOrder(childrenByParent.get(parent.code)));
    setDialogOpen(true);
  };

  const openEdit = (view: MarketView) => {
    setEditingView(view);
    setCreateParentView(null);
    setDialogOpen(true);
  };

  const openManageSymbols = (view: MarketView) => {
    setManagingView(view);
    setSymbolsDialogOpen(true);
  };

  // 新建保存后紧接着打开「管理币种」：新视图默认启用且规则为空（= 匹配全部币种），
  // 直接把管理员送到配置规则/成员的地方，而不是把一个未配置的标签页留在线上。
  const handleEditorSaved = async (createdCode?: string) => {
    const rows = await loadViews();
    if (!createdCode) return;
    const created = rows?.find((v) => v.code === createdCode);
    if (created) openManageSymbols(created);
  };

  const renderRow = (view: MarketView, depth: number) => {
    const siblings = childrenByParent.get(view.parentCode ?? null) ?? [];
    const index = siblings.findIndex((s) => s.code === view.code);
    // 视觉缩进最多到第三层，避免更深层级无限右移
    const indentLevel = Math.min(depth, 2);

    return (
      <TableRow key={view.id} hover>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Stack direction="row" spacing={0}>
            <IconButton
              size="small"
              disabled={index <= 0}
              onClick={() => handleMove(view, -1)}
              aria-label={translate('module.perpsV1Views.columns.order')}
            >
              <ArrowUpwardRounded fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={index < 0 || index >= siblings.length - 1}
              onClick={() => handleMove(view, 1)}
              aria-label={translate('module.perpsV1Views.columns.order')}
            >
              <ArrowDownwardRounded fontSize="small" />
            </IconButton>
          </Stack>
        </TableCell>
        <TableCell sx={{ pl: 2 + indentLevel * 3 }}>
          <Typography variant="body2" fontWeight={depth === 0 ? 700 : 400}>
            {displayLabel(view.label, view.code)}
          </Typography>
          {view.label?.zh ? (
            <Typography variant="caption" color="text.secondary">
              {view.label.zh}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontFamily="monospace">
            {view.code}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {view.isSystem ? (
              <Chip size="small" color="default" label={translate('module.perpsV1Views.systemChip')} />
            ) : null}
            {view.badge && displayLabel(view.badge, '') ? (
              <Chip size="small" color="primary" variant="outlined" label={displayLabel(view.badge, '')} />
            ) : null}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {ruleSummary(view, translate)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{sortSummary(view, translate)}</Typography>
        </TableCell>
        <TableCell>
          <PermissionButton resource={PERPS_V1_VIEWS_PERMISSION} action="update">
            <Switch
              size="small"
              checked={view.enabled}
              onChange={(e) => handleToggleEnabled(view, e.target.checked)}
            />
          </PermissionButton>
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
            <PermissionButton resource={PERPS_V1_VIEWS_PERMISSION} action="update">
              <IconButton size="small" onClick={() => openEdit(view)}>
                <Iconify icon="eva:edit-2-outline" />
              </IconButton>
            </PermissionButton>
            <PermissionButton resource={PERPS_V1_VIEWS_PERMISSION} action="update">
              {/* Tree is capped at three levels (0-based depth 0..2); a row already at depth 2
                  is the deepest level, so adding a child there would make a 4th level, which the
                  backend rejects with a 502 — disable up front instead of surfacing that error. */}
              {depth >= 2 ? (
                <Tooltip title={translate('module.perpsV1Views.addChildDisabledTooltip')}>
                  <span>
                    <IconButton size="small" disabled>
                      <Iconify icon="eva:plus-outline" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={translate('module.perpsV1Views.addChildAction')}>
                  <IconButton
                    size="small"
                    onClick={() => openCreateChild(view)}
                    aria-label={translate('module.perpsV1Views.addChildAction')}
                  >
                    <Iconify icon="eva:plus-outline" />
                  </IconButton>
                </Tooltip>
              )}
            </PermissionButton>
            <PermissionButton resource={PERPS_V1_VIEWS_PERMISSION} action="update">
              <Tooltip title={translate('module.perpsV1Views.manageSymbolsAction')}>
                <IconButton
                  size="small"
                  onClick={() => openManageSymbols(view)}
                  aria-label={translate('module.perpsV1Views.manageSymbolsAction')}
                >
                  <Iconify icon="eva:list-outline" />
                </IconButton>
              </Tooltip>
            </PermissionButton>
            {view.isSystem ? (
              <Tooltip title={translate('module.perpsV1Views.deleteBlockedSystem')}>
                <span>
                  <IconButton size="small" disabled>
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <PermissionButton resource={PERPS_V1_VIEWS_PERMISSION} action="delete">
                <IconButton size="small" color="error" onClick={() => handleDelete(view)}>
                  <Iconify icon="eva:trash-2-outline" />
                </IconButton>
              </PermissionButton>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  // 深度优先递归渲染：每个节点后紧跟其子节点，层级由 depth 累加。
  // depth 上限防御性截断递归：正常数据最多三层，若后端数据异常（如 code 重复
  // 造成查找环）也不会栈溢出导致整屏白屏。
  const renderNode = (view: MarketView, depth: number): JSX.Element => (
    <Fragment key={view.id}>
      {renderRow(view, depth)}
      {depth < 2 &&
        (childrenByParent.get(view.code) ?? []).map((child) => renderNode(child, depth + 1))}
    </Fragment>
  );

  return (
    <PermissionGuard resource={PERPS_V1_VIEWS_PERMISSION} action="read">
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.perpsV1Views.title')}
          links={[{ name: translate('resource.perpsV1') }, { name: translate('resource.perpsV1Navigation') }]}
          action={
            <PermissionButton resource={PERPS_V1_VIEWS_PERMISSION} action="create">
              <LoadingButton variant="contained" onClick={openCreate}>
                {translate('module.perpsV1Views.newView')}
              </LoadingButton>
            </PermissionButton>
          }
          sx={{ mb: 1 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {translate('module.perpsV1Views.subtitle')}
        </Typography>

        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 88 }}>
                    {translate('module.perpsV1Views.columns.order')}
                  </TableCell>
                  <TableCell>{translate('module.perpsV1Views.columns.label')}</TableCell>
                  <TableCell>{translate('module.perpsV1Views.columns.code')}</TableCell>
                  <TableCell>{translate('module.perpsV1Views.columns.badges')}</TableCell>
                  <TableCell>{translate('module.perpsV1Views.columns.rule')}</TableCell>
                  <TableCell>{translate('module.perpsV1Views.columns.sort')}</TableCell>
                  <TableCell>{translate('module.perpsV1Views.columns.enabled')}</TableCell>
                  <TableCell align="right">{translate('module.perpsV1Shared.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && topLevel.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {translate('module.perpsV1Views.empty')}
                    </TableCell>
                  </TableRow>
                ) : null}
                {topLevel.map((view) => renderNode(view, 0))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <ViewEditorDialog
          open={dialogOpen}
          view={editingView}
          createParentView={createParentView}
          createDisplayOrder={createDisplayOrder}
          parentOptions={parentOptions}
          childrenCodes={childrenCodes}
          onClose={() => setDialogOpen(false)}
          onSaved={handleEditorSaved}
        />

        <ViewSymbolsDialog
          open={symbolsDialogOpen}
          view={managingView}
          onClose={() => setSymbolsDialogOpen(false)}
          onSaved={loadViews}
        />
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
